<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import api from '$lib/api/ecom/shopify';
	import { gql } from 'graphql-request';
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';

	import { getText } from '$lib/util/translations';
	import ProductCard from '$lib/components/ecom/ProductCard.svelte';

	import Text from '$lib/components/Text.svelte';
	import Link from '$lib/components/navigation/Link.svelte';
	import Button from '$lib/components/util/Button.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import ProductCardPlaceholder from '$lib/components/ecom/ProductCardPlaceholder.svelte';
	import Header from '../layout/Header.svelte';
	import Dropdown from '$lib/components/inputs/Dropdown.svelte';
	import RangeSlider from 'svelte-range-slider-pips';

	import { browser } from '$app/environment';
	import scrollAction from '$lib/actions/useScrollAction';

	export let data: any;

	/*********************
	 * Variables
	 *********************/

	let products = [];
	let defaultPriceRange = [0, data.maxPrice ?? 300];
	let priceRange = [0, data.maxPrice ?? 300];

	let reachedEnd = false;
	let loading = false;
	let initialized = false;
	let loadedProductSum = 0;

	let filterCollection = data.collection?.handle ?? 'all';
	let filterSize = '';
	let filterTag = '';
	let filterSearch = '';
	let filterTaste: string[] = [];
	let filterRoast: number[] = [];
	let filterAvailable = [];
	let selectedOrder = 0;
	let filtersOpen = false;

	let loadCounter = 0;
	let showLoadButton = false;

	let y: number;
	let lastY: number;
	let headerCollapsed: boolean = false;

	let changeTimer: any;

	let orderOptions = [
		{
			sortKey: 'BEST_SELLING',
			sortKeyCollection: 'BEST_SELLING',
			reverse: false,
			title: getText($page.data.globalTexts, 'ecom_sort_best_selling').text
		},
		{
			sortKey: 'CREATED_AT',
			sortKeyCollection: 'CREATED',
			reverse: false,
			title: getText($page.data.globalTexts, 'ecom_sort_most_recent').text
		},
		{
			sortKey: 'PRICE',
			sortKeyCollection: 'PRICE',
			reverse: true,
			title: getText($page.data.globalTexts, 'ecom_sort_price_descending').text
		},
		{
			sortKey: 'PRICE',
			sortKeyCollection: 'PRICE',
			reverse: false,
			title: getText($page.data.globalTexts, 'ecom_sort_price_ascending').text
		}
	];

	/*********************
	 * Reactivity
	 *********************/
	$: {
		let delta = y - lastY;

		headerCollapsed = y > 0 && delta > 0;
		lastY = y;
	}

	$: standardCollections =
		data.collections?.filter((c: any) => c.node?.hidden?.value !== 'true').sort(sortCollection) ??
		[];
	$: specialCollections =
		data.collections?.filter((c: any) => c.node?.special?.value === 'true') ?? [];

	$: onChange(
		priceRange,
		filterSearch,
		filterCollection,
		filterTag,
		filterSize,
		filterTaste,
		filterRoast
	);

	$: $page.url.searchParams.get('q') && onSearch();
	$: filterCollection = $page.params.slug ?? 'all';

	onMount(async () => {
		init();
	});

	let topOfSection: HTMLDivElement = undefined;

	function init() {
		loading = true;
		filterCollection = $page.params.slug ?? 'all';
		filterSearch = $page.url.searchParams.get('q');

		if ($page.url.searchParams.get('min')) {
			setMinPrice({ target: { value: $page.url.searchParams.get('min') } });
		}
		if ($page.url.searchParams.get('max')) {
			setMaxPrice({ target: { value: $page.url.searchParams.get('max') } });
		}
		if ($page.url.searchParams.get('tag')) {
			filterTag = $page.url.searchParams.get('tag') ?? '';
		}
		if ($page.url.searchParams.get('size')) {
			filterSize = $page.url.searchParams.get('size') ?? '';
		}
		if ($page.url.searchParams.get('taste')) {
			filterTaste = $page.url.searchParams.get('taste')?.split(',') ?? [];
		}
		if ($page.url.searchParams.get('roast')) {
			filterRoast =
				$page.url.searchParams
					.get('roast')
					?.split(',')
					.map((item) => parseInt(item)) ?? [];
		}

		/*await fetchProducts(true);*/
		initialized = true;
	}

	function onSearch() {
		init();
		onChange();
	}

	function onChange(...args: any[]) {
		products = [];
		loading = true;
		clearTimeout(changeTimer);
		changeTimer = setTimeout(() => {
			if (initialized) {
				fetchProducts(true);
				replaceUrl();
			}
		}, 300);
	}

	/*********************
	 * Functions
	 *********************/

	function sortCollection(a, b) {
		if (a.node.handle === 'all') return -1;
		if (b.node.handle === 'all') return 1;

		if (a.node.sort > b.node.sort) {
			return 1;
		}
		if (a.node.sort < b.node.sort) {
			return -1;
		}
		return 0;
	}

	function setCollection(collection, reload = true) {
		filterSearch = null;
		filterCollection = collection.node.handle;
	}

	function setMinPrice(event: any) {
		const value = isNaN(parseInt(event.target.value)) ? 0 : parseInt(event.target.value);

		if (value > data.maxPrice) {
			priceRange[0] = data.maxPrice;
		} else {
			priceRange[0] = value;
		}
	}

	function setMaxPrice(event: any) {
		const value = isNaN(parseInt(event.target.value))
			? data.maxPrice
			: parseInt(event.target.value);

		if (value > data.maxPrice) {
			priceRange[1] = data.maxPrice;
		} else {
			priceRange[1] = value;
		}
	}

	function toggleTaste(taste: string) {
		if (filterTaste.includes(taste)) {
			filterTaste = filterTaste.filter((item) => item !== taste);
		} else {
			filterTaste.push(taste);
			filterTaste = filterTaste;
		}
	}

	function toggleRoast(roast: number) {
		if (filterRoast.includes(roast)) {
			filterRoast = filterRoast.filter((item) => item !== roast);
		} else {
			filterRoast.push(roast);
			filterRoast = filterRoast;
		}
	}

	function replaceUrl() {
		if (!browser) {
			return;
		}

		let url = '/' + $page.params.lang + '/shop';

		if (filterCollection) {
			url += '/' + filterCollection;
		}

		let prevParameter = false;

		if (priceRange[0] !== defaultPriceRange[0] || priceRange[1] !== defaultPriceRange[1]) {
			url += prevParameter ? '&min=' : '?min=';
			prevParameter = true;
			url += priceRange[0] + '&max=' + priceRange[1];
		}

		if (filterTag) {
			url += prevParameter ? '&tag=' : '?tag=';
			url += filterTag;
			prevParameter = true;
		}

		if (filterSize) {
			url += prevParameter ? '&size=' : '?size=';
			url += filterSize;
			prevParameter = true;
		}

		if (filterTaste?.length > 0) {
			url += prevParameter ? '&taste=' : '?taste=';
			url += filterTaste.join(',');
			prevParameter = true;
		}

		if (filterRoast?.length > 0) {
			url += prevParameter ? '&roast=' : '?roast=';
			url += filterRoast.join(',');
			prevParameter = true;
		}

		if (filterSearch) {
			url += prevParameter ? '&q=' : '?q=';
			url += filterSearch;
			prevParameter = true;
		}

		// replaceState('', query);

		goto(url, {
			replaceState: true,
			keepFocus: true,
			noScroll: true
		});

		// history.replaceState({}, '', url);
	}

	function resetFilters() {
		filterSize = '';
		filterTag = '';
		filterSearch = '';
		filterTaste = [];
		filterRoast = [];
		priceRange = defaultPriceRange;
		filterCollection = 'all';
	}

	async function fetchProducts(clear = false, cursor = null, amount = 32) {
		/*if (loading) {
			return;
		}*/

		if (clear && typeof window !== 'undefined') {
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		}

		if (cursor === null && products.length > 0) {
			cursor = products[products.length - 1].cursor;
		}

		if (initialized && clear) {
			cursor = null;
		}

		showLoadButton = false;
		loading = true;

		let sortKey;
		let reverse;

		if (filterSearch) {
			sortKey = 'RELEVANCE';
			reverse = false;
		} else if (filterCollection) {
			sortKey = orderOptions[selectedOrder].sortKeyCollection;
			reverse = orderOptions[selectedOrder].reverse;
		} else {
			sortKey = orderOptions[selectedOrder].sortKey;
			reverse = orderOptions[selectedOrder].reverse;
		}

		let productData;
		if (filterCollection && !filterSearch) {
			productData = await api.getCollectionProducts(
				filterCollection,
				cursor,
				amount,
				'MANUAL',
				reverse,
				$page.data.location,
				$page.params.lang
			);
			reachedEnd = !productData?.collection.products.pageInfo.hasNextPage;
		} else {
			productData = await api.searchProducts(
				filterSearch,
				cursor,
				amount,
				'RELEVANCE',
				reverse,
				$page.data.location,
				$page.params.lang
			);
			reachedEnd = !productData?.pageInfo?.hasNextPage;
		}

		if (clear) {
			products = [];
		}

		for (const product of productData?.collection.products?.edges ?? []) {
			let valid = true;
			if (!product.node.availableForSale && filterAvailable.length > 0) {
				valid = false;
			}

			if (product.node.product_type?.value !== $page.data.customerType) {
				valid = false;
			}

			const minPrice = parseFloat(product.node.priceRange.minVariantPrice.amount);
			const maxPrice = parseFloat(product.node.priceRange.maxVariantPrice.amount);

			if (
				(minPrice < priceRange[0] || minPrice > priceRange[1]) &&
				(maxPrice < priceRange[0] || maxPrice > priceRange[1])
			) {
				valid = false;
			}

			if (
				filterTag &&
				!product.node.tags
					.map((item: any) => item.toLocaleLowerCase())
					.includes(filterTag.toLocaleLowerCase())
			) {
				valid = false;
			}

			if (filterSize) {
				let hasSizeOption = false;
				for (const variant of product.node?.variants?.edges ?? []) {
					for (const option of variant.node?.selectedOptions ?? []) {
						if (option.value === filterSize) {
							hasSizeOption = true;
						}
					}
				}
				if (!hasSizeOption) {
					valid = false;
				}
			}

			if (filterTaste.length > 0) {
				let hasTaste = false;
				for (const taste of product.node?.tasting_notes?.references?.edges ?? []) {
					if (filterTaste.includes(taste.node?.handle)) {
						hasTaste = true;
						break;
					}
				}
				if (!hasTaste) {
					valid = false;
				}
			}

			if (filterTaste.length > 0) {
				let hasTaste = false;
				for (const taste of product.node?.tasting_notes?.references?.edges ?? []) {
					if (filterTaste.includes(taste.node?.handle)) {
						hasTaste = true;
						break;
					}
				}
				if (!hasTaste) {
					valid = false;
				}
			}

			if (filterRoast.length > 0) {
				if (!filterRoast.includes(parseInt(product.node?.roast_level?.value ?? -1))) {
					valid = false;
				}
			}

			if (valid) {
				products.push(product);
				loadedProductSum++;
			}
		}

		products = products;
		loading = false;

		if (loadCounter >= 1) {
			showLoadButton = true;
		}

		if (!reachedEnd) {
			showLoadButton = false;
		}

		if (!reachedEnd) {
			// If all products were filered out but more are available, fetch again
			const lastLoaded =
				productData?.collection.products?.edges[productData.collection.products.edges.length - 1];
			amount *= 2;
			if (amount > 250) {
				amount = 250;
			}
			const endCursor = productData?.collection?.products?.pageInfo?.endCursor;
			if (endCursor) {
				fetchProducts(false, endCursor, amount);
			}
		} else {
			loadedProductSum = 0;
		}
	}

	function doSearch(_) {
		filterCollection = null;
	}

	let isOverflowing = false;

	const checkOverflow = () => {
		const collectionsWrapper = document.querySelector('.collections') as HTMLElement;
		if (collectionsWrapper) {
			isOverflowing = collectionsWrapper.scrollWidth > collectionsWrapper.clientWidth;
		}
	};

	const scrollRight = () => {
		const collectionsWrapper = document.querySelector('.collections') as HTMLElement;
		if (collectionsWrapper) {
			collectionsWrapper.scrollBy({ left: 200, behavior: 'smooth' });
		}
	};

	onMount(() => {
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	});

	let showTasteFilter = true;
	let showRoastFilter = true;
	let showSizeFilter = true;

	$: {
		for (const collection of standardCollections) {
			if (collection.node.handle === filterCollection) {
				showRoastFilter = collection.node?.show_filters?.value === 'true';
				showTasteFilter = collection.node?.show_filters?.value === 'true';
				showSizeFilter = collection.node?.show_filters?.value === 'true';
			}
		}
	}

	$: hasFilter =
		filterSearch ||
		filterSize ||
		filterTag ||
		filterTaste.length > 0 ||
		filterRoast.length > 0 ||
		priceRange[0] !== defaultPriceRange[0] ||
		priceRange[1] !== defaultPriceRange[1];
</script>

<div bind:this={topOfSection} />
<svelte:window bind:scrollY={y} />
{#key $page.params.lang}
	<div class="section-product-grid">
		<Section>
			<div class="section-wrapper">
				<div class="collections-wrapper">
					<div class="collections">
						{#each standardCollections as collection, idx}
							{#key 'collection-' + idx}
								<div
									class="collection"
									class:active={filterCollection === collection.node.handle ||
										(filterCollection === null && collection.node.handle === 'all')}
									on:click={() => setCollection(collection)}
								>
									{#if collection.node.image_inactive?.reference?.image?.url}
										<img
											class="icon inactive"
											src={collection.node.image_inactive?.reference?.image?.url}
											loading="lazy"
											alt=""
										/>
									{/if}
									{#if collection.node.image_active?.reference?.image?.url}
										<img
											class="icon active"
											src={collection.node.image_active?.reference?.image?.url}
											loading="lazy"
											alt=""
										/>
									{/if}
									<span>{collection.node.title}</span>
								</div>
							{/key}
						{/each}
						{#if isOverflowing}
							<div class="scroll-arrow" on:click={scrollRight}>
								<GlobalMedia key="icon_caret_grey" />
							</div>
						{/if}
					</div>
				</div>
				{#if filterSearch}
					<div class="search-title">
						<Text key="ecom_results_for" tag="span" />{' ' + filterSearch}
					</div>
				{/if}
				<div class="grid-layout">
					<div class="sidebar-container">
						<div class="sidebar" class:open={filtersOpen}>
							<button
								class="sidebar-toggle"
								class:open={!filtersOpen}
								on:click={() => {
									filtersOpen = !filtersOpen;
								}}
							>
								{#if !filtersOpen}
									<div class="toggle-text"><Text key="ecom_filter" /></div>
									<div class="toggle-icon"><GlobalMedia key="icon-filter" /></div>
								{:else}
									<div class="toggle-close">
										<div class="wrapper"><GlobalMedia key="icon-close" /></div>
									</div>
								{/if}
							</button>
							<div class="sidebar-content" class:open={filtersOpen}>
								<div class="group">
									<div class="title">
										<Text key="ecom_filter" />
									</div>
									<div class="dropdown">
										<Dropdown type="small" bind:value={filterTag}>
											<option value=""><Text key="ecom_all" /></option>
											{#each $page.data.metaObjects?.tag_filters.edges as size}
												<option value={size.node.value?.value}>{size.node.value?.value}</option>
											{/each}
										</Dropdown>
									</div>
								</div>
								{#if showSizeFilter}
									<div class="group">
										<div class="title">
											<Text key="ecom_size" />
										</div>
										<div class="dropdown">
											<Dropdown type="small" bind:value={filterSize}>
												<option value=""><Text key="ecom_all" /></option>
												{#each $page.data.metaObjects?.size_filters.edges as size}
													<option value={size.node.value?.value}>{size.node.value?.value}</option>
												{/each}
											</Dropdown>
										</div>
									</div>
								{/if}
								{#if showTasteFilter}
									<div class="group">
										<div class="title">
											<Text key="ecom_taste" />
										</div>
										<div class="selection">
											{#each $page.data.metaObjects?.tasting_notes?.edges?.sort((a, b) => (a?.node?.order?.value ?? 0) - (b?.node?.order?.value ?? 0)) as taste}
												<button
													type="button"
													class="select-button"
													class:active={filterTaste.includes(taste.node?.handle)}
													on:click={() => {
														toggleTaste(taste.node?.handle);
													}}>{taste.node?.label?.value}</button
												>
											{/each}
										</div>
									</div>
								{/if}
								{#if showRoastFilter}
									<div class="group">
										<div class="title">
											<Text key="ecom_roast" />
										</div>
										<div class="selection">
											{#each Array(5) as _, i}
												<button
													type="button"
													class="select-button"
													class:active={filterRoast.includes(i + 1)}
													on:click={() => {
														toggleRoast(i + 1);
													}}
												>
													<Text key={'roast_level__' + (i + 1)} />
												</button>
											{/each}
										</div>
									</div>
								{/if}
								<div class="group">
									<div class="title">
										<Text key="ecom_price" />
									</div>
									<div class="range">
										<RangeSlider
											bind:values={priceRange}
											float
											range
											min={defaultPriceRange[0]}
											max={defaultPriceRange[1]}
										/>
										<div class="range-container">
											<input
												type="text"
												class="range-value"
												value={priceRange[0] + ' €'}
												on:change={(e) => setMinPrice(e)}
											/>
											<div class="line" />
											<input
												type="text"
												class="range-value"
												value={priceRange[1] + ' €'}
												on:change={(e) => setMaxPrice(e)}
											/>
										</div>
									</div>
								</div>
								<div class="reset">
									<button type="button" on:click={() => resetFilters()}>
										<Text key="ecom_reset_filter" />
									</button>
								</div>
							</div>
						</div>
					</div>
					<div class="grid-wrapper">
						<div class="grid">
							{#each products as product, idx}
								{#key product.node.id}
									<ProductCard
										product={product.node}
										{filterSize}
										{priceRange}
										class="fade-up started"
										style="animationDelay: {idx * 100}ms"
									/>
								{/key}
							{/each}
							{#if loading}
								{#each Array(12) as _, i}
									{#key 'placeholder-' + i}
										<ProductCardPlaceholder />
									{/key}
								{/each}
							{/if}
						</div>
						{#if products.length === 0 && !loading && initialized}
							<div class="no-products">
								<Text
									key={hasFilter || filterCollection === 'all'
										? 'ecom_no_products'
										: 'ecom_coming_soon'}
								/>
							</div>
						{/if}
					</div>
				</div>
			</div>
			<div class="load-more-wrapper">
				{#if showLoadButton}
					<Button on:click={fetchProducts} textKey="ecom_load_more" />
				{/if}
			</div>
		</Section>
	</div>
{/key}

<style lang="scss">
	.section-product-grid :global(section) {
		margin-top: 0 !important;
	}

	.section-wrapper {
		display: flex;
		flex-direction: column;
		min-height: 650px;
		margin-top: v(content-padding-xl);

		@media (max-width: 900px) {
			margin-top: v(content-padding);
		}

		.collections-wrapper {
			@media (max-width: 1200px) {
				margin-inline: calc(-1 * v(page-padding));
				width: calc(100% + 2 * v(page-padding));
				position: relative;
			}
		}

		.collections {
			display: flex;
			justify-content: space-between;
			padding: v(content-padding-sm) v(content-padding-lg);
			background: color(white);
			border-radius: 4px;
			gap: v(content-padding);
			width: 100%;

			@media (max-width: 1200px) {
				overflow-x: scroll;
				background: transparent;
				padding: 0;
				padding-inline: v(page-padding);
				gap: v(content-padding-xs);

				::-webkit-scrollbar {
					display: none;
				}
				-ms-overflow-style: none; /* IE and Edge */
				scrollbar-width: none; /* Firefox */

				&:global(.reached-horizontal-end) {
					:global(.scroll-arrow) {
						opacity: 0;
					}
				}
			}

			.collection {
				display: flex;
				justify-content: flex-start;
				align-items: center;
				gap: v(content-gap-sm);
				border: 0;
				background: transparent;
				hyphens: auto;
				width: 100%;
				@include font(h5-collection);

				cursor: pointer;

				&:first-child {
					width: 40%;
				}

				span {
					text-decoration-color: #fff0;
					// text-underline-offset: 0.5em;
					transition: all 0.3s ease;
					margin-top: 0.3rem;
					text-align: start;
					max-width: 10ch;
					@include media(phone) {
						margin-top: 0;
					}
				}

				.icon {
					@include transition(all);
					width: 2rem;
					height: 2rem;
				}

				.icon.active {
					opacity: 0;
				}

				.icon.inactive {
					position: absolute;
				}

				&.active,
				&:hover {
					span {
						text-decoration: underline;
					}

					.icon.inactive {
						opacity: 0;
					}
					.icon.active {
						opacity: 1;
					}
				}

				@media (max-width: 1200px) {
					&.active,
					&:hover {
						background: color(yellow);
						border: 1px solid color(yellow);

						span {
							text-decoration: none !important;
							text-decoration-color: transparent;
							max-width: 100%;
						}
					}

					@include font(h6);
					padding: 8px 16px 8px 16px;
					border-radius: 4px;
					border: 1px solid color(black);
					flex-basis: 0;
					width: 100%;
					transition: all 0.3s ease;

					.icon,
					.icon.active,
					.icon.inactive {
						display: none !important;
					}

					span {
						white-space: nowrap;
						max-width: 100%;
						transition: all 0.3s ease;
					}
				}
			}
		}

		.search-title {
			margin-top: v(content-padding-xl);
			margin-bottom: v(content-padding);
			@include font(h3);
		}

		.grid-layout {
			display: grid;
			grid-template-columns: 280px 1fr;
			gap: v(content-padding-sm);
			margin-top: v(content-padding-sm);

			@media (max-width: 1400px) {
				grid-template-columns: 220px 1fr;
			}

			@media (max-width: 900px) {
				grid-template-columns: 1fr;
				margin-top: v(content-padding);
				gap: v(content-padding);
			}

			@include media(phone) {
				grid-template-columns: 1fr;
				grid-template-rows: auto 1fr;
			}
		}

		.sidebar-container {
			.sidebar {
				width: 100%;
				background: color(yellow_01);
				position: sticky;
				border-radius: 4px;

				max-height: calc(100vh - 2 * v(content-gap));
				max-height: calc(100dvh - 2 * v(content-gap));

				overflow-y: auto;
				top: v(content-gap);
				padding: v(content-padding);
				transition: all 0.3s ease;

				.sidebar-toggle {
					display: none;
					align-items: center;
					justify-content: space-between;
					cursor: pointer;
					-webkit-tap-highlight-color: transparent;
					border: 0;
					background: transparent;
					width: 100%;
					@include font(text-sm);

					.toggle-text {
						margin-top: 0.3rem;
						margin-inline-end: 16px;
						display: flex;
						align-items: center;
					}

					.toggle-icon {
						width: 20px;
						height: 20px;
						object-fit: contain;
						display: flex;
						align-items: center;
					}

					.toggle-close {
						display: flex;
						justify-content: flex-end;
						width: 100%;

						.wrapper {
							width: 18px;
						}
					}
				}

				@media (max-width: 900px) {
					padding: v(content-padding-xs) v(content-padding-sm);

					&.open {
						padding: v(content-padding-sm);
					}

					.sidebar-toggle {
						display: flex;
					}

					.sidebar-content {
						display: none;

						&.open {
							display: block;
						}
					}
				}

				.group {
					margin-bottom: v(content-padding);
				}

				.title {
					@include font(h6);
					margin-bottom: 6px;
				}

				:global(select) {
					background: color(yellow_03);
				}

				.selection {
					display: flex;
					flex-wrap: wrap;
					gap: 8px;

					.select-button {
						padding: 3px 12px 1px 12px;
						border: 1px solid color(black);
						border-radius: 4px;
						color: color(black);
						background: transparent;
						transition: all 0.3s ease;
						-webkit-tap-highlight-color: transparent;
						@include font(text-sm-filter);

						&:hover,
						&.active {
							background: color(black);
							color: color(white);
						}
					}
				}

				.range {
					--range-range: black;
					--range-range-inactive: black;
					--range-handle: black;
					--range-handle-inactive: black;
					--range-handle-focus: black;
					font-size: 0.6rem;

					:global(.rangeFloat) {
						font-size: 1rem;
					}

					.range-container {
						display: flex;
						align-items: center;
						justify-content: space-between;
						margin-top: 16px;
						opacity: 0.7;
						width: 100%;

						.range-value {
							border: 0;
							border-radius: 4px;
							padding: 8px;
							width: 40%;
							text-align: center;
							color: color(black);
							background: color(yellow_03);
							@include font(text-sm);
						}

						.line {
							width: 8px;
							height: 1px;
							background: color(black);
							margin: 0 8px;
						}
					}
				}
				.reset {
					button {
						background: transparent;
						border: 0;
						color: color(black);
						text-decoration: underline;
						@include font(h6);
						text-transform: uppercae;
						padding: 0;
					}
				}
			}
		}

		.grid {
			width: 100%;
			display: grid;
			grid-template-columns: repeat(4, minmax(0, 1fr));
			grid-gap: v(content-padding-xl) v(content-padding-sm);

			@media (max-width: 1750px) {
				grid-template-columns: repeat(3, minmax(0, 1fr));
			}

			@media (max-width: 1400px) {
				grid-template-columns: repeat(2, minmax(0, 1fr));
			}

			@media (max-width: 900px) {
				grid-gap: v(content-padding) v(content-padding-sm);
			}
		}
	}

	.no-products {
		width: 100%;
		display: flex;
		justify-content: center;
		padding: v(content-padding-xl);
	}
	.collections-wrapper {
		display: flex;
		justify-content: center;
		width: 100%;
		align-items: center;
	}

	.scroll-arrow {
		@include transition;
		display: flex;
		background: transparent;
		font-size: 1.5rem;
		position: absolute;
		inset-inline-end: 0;
		z-index: 1;
		width: 40px;
		height: 100%;
		display: flex;
		justify-content: flex-end;
		align-items: center;

		:global(svg) {
			height: 16px;
			width: 16px;
			margin-inline-end: 8px;
		}

		background: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0) 0%,
			color(background) 25%,
			color(background) 100%
		);
	}
</style>
